import { useRef, useState } from "react";
import type { UserRow } from "./sql";
import { FUNNEL_STEPS } from "./sql";
import { PRIMARY, STEEL, FG1, MUTED } from "../../lib/funnel/theme";
import { SectionCardHeader } from "../../components/ChartCard";
import type { FilterPillSpec } from "../../components/FilterPills";

type PathFilter = "all" | "demo" | "self";

type Column = { label: string; predicate: (u: UserRow) => boolean };

function buildColumns(splitPaid: boolean): Column[] {
  const cols: Column[] = [];
  cols.push({ label: "Signed up", predicate: () => true });
  for (const s of FUNNEL_STEPS) {
    if (s.label === "Converted to paid" && splitPaid) {
      cols.push({ label: "Converted to Plus", predicate: (u) => u.on_paid_plan && u.billing_plan === "plus" });
      cols.push({ label: "Converted to Pro", predicate: (u) => u.on_paid_plan && u.billing_plan === "pro" });
    } else {
      cols.push({ label: s.label, predicate: s.predicate });
    }
  }
  return cols;
}

function userName(u: UserRow): string {
  return `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim();
}

function userTitle(u: UserRow): string {
  const name = userName(u);
  const head = name ? `${name} (${u.email})` : u.email;
  return u.company_name ? `${head}, ${u.company_name}` : head;
}

export function FunnelColumnLists({
  users,
  splitPaid = false,
  title = "Users by funnel column",
  highlightedUserId,
  onHighlightUser,
  pagePills,
}: {
  users: UserRow[];
  splitPaid?: boolean;
  title?: string;
  highlightedUserId?: string | null;
  onHighlightUser?: (id: string | null) => void;
  pagePills?: FilterPillSpec[];
}) {
  const [pathFilter, setPathFilter] = useState<PathFilter>("all");
  const [search, setSearch] = useState("");
  const columns = buildColumns(splitPaid);
  const q = search.trim().toLowerCase();
  const matchesSearch = (u: UserRow): boolean => {
    if (!q) return true;
    const name = `${u.first_name ?? ""} ${u.last_name ?? ""}`.toLowerCase();
    return name.includes(q) || u.email.toLowerCase().includes(q) || (u.company_name ?? "").toLowerCase().includes(q);
  };
  const filteredUsers = q ? users.filter(matchesSearch) : users;
  const sortPinned = (arr: UserRow[]): UserRow[] => {
    if (!highlightedUserId) return arr;
    const idx = arr.findIndex((u) => u.external_id === highlightedUserId);
    if (idx <= 0) return arr;
    const next = arr.slice();
    const [pinned] = next.splice(idx, 1);
    if (pinned) next.unshift(pinned);
    return next;
  };
  const demo = sortPinned(pathFilter === "self" ? [] : filteredUsers.filter((u) => u.had_demo));
  const self = sortPinned(pathFilter === "demo" ? [] : filteredUsers.filter((u) => !u.had_demo));
  const sectionRef = useRef<HTMLElement | null>(null);

  const handleClick = (id: string) => {
    const next = highlightedUserId === id ? null : id;
    onHighlightUser?.(next);
    if (next && sectionRef.current) {
      sectionRef.current.querySelectorAll<HTMLElement>(".funnel-col-list").forEach((el) => {
        el.scrollTop = 0;
      });
    }
  };

  const highlightedUser = highlightedUserId
    ? users.find((u) => u.external_id === highlightedUserId)
    : null;
  const selectionLabel = highlightedUser
    ? (userName(highlightedUser) ? `${userName(highlightedUser)} (${highlightedUser.email})` : highlightedUser.email)
    : null;

  const pills: FilterPillSpec[] = [...(pagePills ?? [])];
  if (pathFilter !== "all") {
    pills.push({ kind: "mode", label: pathFilter === "demo" ? "Path: demo only" : "Path: self-serve only" });
  }
  if (selectionLabel) {
    pills.unshift({
      kind: "selection",
      label: `Selected: ${selectionLabel}`,
      onClear: () => onHighlightUser?.(null),
    });
  }

  return (
    <section className="card" ref={sectionRef}>
      <SectionCardHeader
        title={title}
        subtitle="One list per funnel column. Format: Name (email)."
        filterPills={pills}
        headerExtra={
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <span className="input-wrap" style={{ minWidth: 220 }}>
              <i className="ti ti-search" />
              <input
                type="text"
                className="input"
                placeholder="Search name, email, company"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </span>
            <div role="group" aria-label="Path filter" style={{ display: "inline-flex", border: `1px solid ${MUTED}`, borderRadius: 6, overflow: "hidden", fontSize: 11 }}>
              {([
                { key: "all", label: "All" },
                { key: "demo", label: "Demo" },
                { key: "self", label: "Self" },
              ] as const).map((opt) => {
                const active = pathFilter === opt.key;
                const bg = active ? (opt.key === "self" ? STEEL : PRIMARY) : "transparent";
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setPathFilter(opt.key)}
                    style={{
                      padding: "3px 12px",
                      background: bg,
                      color: active ? "#fff" : FG1,
                      border: "none",
                      cursor: "pointer",
                      fontWeight: active ? 600 : 400,
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        }
      />
      <div className="card-body">
        <div
          className="funnel-cols"
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))`,
            gap: 12,
          }}
        >
          {columns.map((col) => {
            const dAll = demo.filter(col.predicate);
            const sAll = self.filter(col.predicate);
            const pinned = highlightedUserId
              ? (dAll.find((u) => u.external_id === highlightedUserId)
                ?? sAll.find((u) => u.external_id === highlightedUserId)
                ?? null)
              : null;
            const pinnedIsDemo = pinned ? pinned.had_demo : false;
            const d = pinned && pinnedIsDemo ? dAll.filter((u) => u.external_id !== pinned.external_id) : dAll;
            const s = pinned && !pinnedIsDemo ? sAll.filter((u) => u.external_id !== pinned.external_id) : sAll;
            const pinnedDot = pinnedIsDemo ? PRIMARY : STEEL;
            // When a user is highlighted, dim columns where that user does
            // not appear so the absence reads as "not in this stage" rather
            // than "missing".
            const dimmed = !!highlightedUserId && !pinned;
            return (
              <div key={col.label} className="funnel-col">
                <div className="funnel-col-h">{col.label}</div>
                <div className="funnel-col-counts">
                  <span style={{ color: PRIMARY }}><span className="dot" style={{ background: PRIMARY }} /> Demo {dAll.length}</span>
                  <span style={{ color: STEEL }}><span className="dot" style={{ background: STEEL }} /> Self {sAll.length}</span>
                </div>
                <ul className={`funnel-col-list${dimmed ? " is-dimmed" : ""}`}>
                  {dAll.length === 0 && sAll.length === 0 && (
                    <li className="funnel-col-empty">—</li>
                  )}
                  {dAll.length > 0 && (
                    <li className="funnel-col-section" style={{ background: PRIMARY }}>Demo</li>
                  )}
                  {pinned && pinnedIsDemo && (
                    <li
                      key={`p-${pinned.external_id}`}
                      title={userTitle(pinned)}
                      className="is-highlighted"
                      onClick={() => handleClick(pinned.external_id)}
                      style={{ cursor: onHighlightUser ? "pointer" : undefined }}
                    >
                      <span className="path-dot" style={{ background: pinnedDot }} />
                      <span className="funnel-col-text">
                        {userName(pinned) && <span className="funnel-col-name">{userName(pinned)}</span>}
                        <span className="funnel-col-email">{pinned.email}</span>
                      </span>
                    </li>
                  )}
                  {d.map((u) => {
                    const name = userName(u);
                    return (
                      <li
                        key={`d-${u.external_id}`}
                        title={userTitle(u)}
                        className={highlightedUserId === u.external_id ? "is-highlighted" : ""}
                        onClick={() => handleClick(u.external_id)}
                        style={{ cursor: onHighlightUser ? "pointer" : undefined }}
                      >
                        <span className="path-dot" style={{ background: PRIMARY }} />
                        <span className="funnel-col-text">
                          {name && <span className="funnel-col-name">{name}</span>}
                          <span className="funnel-col-email">{u.email}</span>
                        </span>
                      </li>
                    );
                  })}
                  {sAll.length > 0 && (
                    <li className="funnel-col-section" style={{ background: STEEL }}>Self sign-up</li>
                  )}
                  {pinned && !pinnedIsDemo && (
                    <li
                      key={`p-${pinned.external_id}`}
                      title={userTitle(pinned)}
                      className="is-highlighted"
                      onClick={() => handleClick(pinned.external_id)}
                      style={{ cursor: onHighlightUser ? "pointer" : undefined }}
                    >
                      <span className="path-dot" style={{ background: pinnedDot }} />
                      <span className="funnel-col-text">
                        {userName(pinned) && <span className="funnel-col-name">{userName(pinned)}</span>}
                        <span className="funnel-col-email">{pinned.email}</span>
                      </span>
                    </li>
                  )}
                  {s.map((u) => {
                    const name = userName(u);
                    return (
                      <li
                        key={`s-${u.external_id}`}
                        title={userTitle(u)}
                        className={highlightedUserId === u.external_id ? "is-highlighted" : ""}
                        onClick={() => handleClick(u.external_id)}
                        style={{ cursor: onHighlightUser ? "pointer" : undefined }}
                      >
                        <span className="path-dot" style={{ background: STEEL }} />
                        <span className="funnel-col-text">
                          {name && <span className="funnel-col-name">{name}</span>}
                          <span className="funnel-col-email">{u.email}</span>
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

